/* Shared product-card body renderer. The single copy of the image / title /
   rating-stock / price / description / CTA markup shown by any Cube Finder
   -style product card. Consumed today by assets/scs-cube-finder.js (both its
   search and surprise-me render paths); assets/scs-oos-suggestions.js
   (Phase 17) is the second consumer. Styles come from assets/scs-cube-finder.css,
   already loaded globally — this file adds no CSS of its own.

   No DOM access, no listeners, no fetch, no tracking. render() returns a
   plain HTML string and nothing else. Callers own: computing the product
   URL (including any UTM), wiring click listeners against the attribute
   hooks they pass in via opts, and rendering their own chrome around the
   returned body (eyebrow/headline, fallback banner, try-another button,
   feedback block all stay in the consumer).

   The opts attr strings (imgLinkAttr, imgAttr, titleLinkAttr, atcAttr,
   viewAttr) are interpolated raw into markup: pass code-literal constants
   only, never user input or Worker data. */

(() => {

function escape(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str) {
  return escape(str);
}

function stockBadge(inventory) {
  if (typeof inventory !== 'number' || inventory <= 0) return '';
  if (inventory <= 20) {
    // Literal indentation preserved byte-exact from the original class method (REG-05).
    return `<span class="scs-cube-finder__stock scs-cube-finder__stock--low">
        <span class="scs-cube-finder__stock-dot"></span>
        Low stock · only ${inventory} left
      </span>`;
  }
  return `<span class="scs-cube-finder__stock">
      <span class="scs-cube-finder__stock-dot"></span>
      In stock
    </span>`;
}

function starRating(rating, count) {
  if (typeof rating !== 'number' || rating <= 0) return '';
  if (typeof count !== 'number' || count < 3) return ''; // hide low-confidence ratings
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  let stars = '';
  for (let i = 0; i < 5; i++) {
    const cls =
      i < full ? 'is-full' : i === full && half ? 'is-half' : 'is-empty';
    stars += `<span class="scs-cube-finder__star ${cls}" aria-hidden="true"></span>`;
  }
  return `<span class="scs-cube-finder__rating">${stars}<span class="scs-cube-finder__rating-value">${rating.toFixed(1)}</span></span>`;
}

function render(product, opts = {}) {
  const p = product || {};
  const productUrl = opts.productUrl || '';
  const price = typeof p.price === 'number' ? p.price.toFixed(2) : '';
  const stockBadgeHtml = stockBadge(p.inventory);
  const ratingMarkup = starRating(p.rating, p.ratingCount);
  const description = opts.description;
  const descriptionHtml = (opts.descriptionAlways || description) ? `<p class="scs-cube-finder__explanation">${escape(description)}</p>` : '';

  return `
        <div class="scs-cube-finder__product-body">
          <a
            class="scs-cube-finder__product-image-link"
            href="${escapeAttr(productUrl)}"
            target="_blank"
            rel="noopener noreferrer"
            tabindex="-1"
            aria-hidden="true"
            ${opts.imgLinkAttr || ''}
          >
            <div class="scs-cube-finder__product-image">
              <img
                src="${escapeAttr(p.image || '')}"
                alt="${escapeAttr(p.title || '')}"
                loading="lazy"
                ${opts.imgAttr || ''}
              >
            </div>
          </a>
          <div class="scs-cube-finder__product-meta">
            <h4 class="scs-cube-finder__product-title">
              <a
                class="scs-cube-finder__product-title-link"
                href="${escapeAttr(productUrl)}"
                target="_blank"
                rel="noopener noreferrer"
                ${opts.titleLinkAttr || ''}
              >${escape(p.title || '')}</a>
            </h4>
            <div class="scs-cube-finder__product-attrs">
              ${ratingMarkup}
              ${stockBadgeHtml}
            </div>
            <p class="scs-cube-finder__price">$${price}</p>
          </div>
        </div>
        ${descriptionHtml}
        <div class="scs-cube-finder__ctas">
          <button
            type="button"
            class="scs-cube-finder__cta scs-cube-finder__cta--primary"
            ${opts.atcAttr || ''}
          >
            <span aria-hidden="true">+</span> Add to cart
          </button>
          <a
            class="scs-cube-finder__cta scs-cube-finder__cta--secondary"
            href="${escapeAttr(productUrl)}"
            target="_blank"
            rel="noopener noreferrer"
            ${opts.viewAttr || ''}
          >
            View details <span class="scs-cube-finder__cta-arrow" aria-hidden="true">↗</span>
          </a>
        </div>`;
}

window.ScsProductCard = { render, stockBadge, starRating };

})();
